/**
 * Shot mode state — presets, aspect, queue, batch run, pending record.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  NamedShotPresetSchema,
  SEED_SHOT_PRESETS,
  ShotAspectSchema,
  ShotPresetValuesSchema,
  newPresetId,
  type NamedShotPreset,
  type ShotAspect,
  type ShotPresetValues,
} from "@web/features/globe/shotPreset.ts";
import type { ShotSkip, ShotTarget } from "@web/features/globe/shotTargets.ts";
import { z } from "zod";

export type ShotRecordTarget =
  | { kind: "country"; code: string; artistName?: string }
  | { kind: "artist"; id: string };

export type BatchRunStatus = "idle" | "running" | "paused" | "done";

export type BatchProgress = {
  index: number;
  total: number;
  artistName: string;
  countryLabel: string;
};

export type BatchSummary = {
  recorded: number;
  skipped: number;
  outputLabel: string;
  presetName: string;
};

interface GlobeShotState {
  presets: NamedShotPreset[];
  activePresetId: string;
  aspect: ShotAspect;
  panelCollapsed: boolean;
  queueCollapsed: boolean;
  queue: ShotTarget[];
  skipped: ShotSkip[];
  dedupeByCountry: boolean;
  outputFolderName: string | null;
  codecNoticeDismissed: boolean;
  recording: boolean;
  pendingRecord: ShotRecordTarget | null;
  batchStatus: BatchRunStatus;
  batchProgress: BatchProgress | null;
  batchSummary: BatchSummary | null;

  activePreset: () => NamedShotPreset;
  activeValues: () => ShotPresetValues;
  setAspect: (aspect: ShotAspect) => void;
  setPanelCollapsed: (v: boolean) => void;
  setQueueCollapsed: (v: boolean) => void;
  setActivePresetId: (id: string) => void;
  updateActiveValues: (patch: Partial<ShotPresetValues>) => void;
  setEndAltitudeForAspect: (aspect: ShotAspect, value: number) => void;
  savePresetAs: (name: string) => void;
  renameActivePreset: (name: string) => void;
  setDedupeByCountry: (v: boolean) => void;
  setOutputFolderName: (name: string | null) => void;
  dismissCodecNotice: () => void;
  setRecording: (v: boolean) => void;
  requestRecord: (target: ShotRecordTarget) => void;
  consumePendingRecord: () => ShotRecordTarget | null;
  setQueue: (queue: ShotTarget[]) => void;
  addTargets: (targets: ShotTarget[], skipped: ShotSkip[]) => void;
  removeFromQueue: (id: string) => void;
  reorderQueue: (from: number, to: number) => void;
  clearQueue: () => void;
  clearSkipped: () => void;
  setBatchStatus: (s: BatchRunStatus) => void;
  setBatchProgress: (p: BatchProgress | null) => void;
  setBatchSummary: (s: BatchSummary | null) => void;
}

const PersistedSchema = z.object({
  presets: z.array(NamedShotPresetSchema).min(1),
  activePresetId: z.string(),
  aspect: ShotAspectSchema,
  panelCollapsed: z.boolean(),
  queueCollapsed: z.boolean(),
  dedupeByCountry: z.boolean(),
  codecNoticeDismissed: z.boolean(),
});

export const useGlobeShotStore = create<GlobeShotState>()(
  persist(
    (set, get) => ({
      presets: SEED_SHOT_PRESETS,
      activePresetId: SEED_SHOT_PRESETS[0]!.id,
      aspect: "9:16",
      panelCollapsed: true,
      queueCollapsed: true,
      queue: [],
      skipped: [],
      dedupeByCountry: true,
      outputFolderName: null,
      codecNoticeDismissed: false,
      recording: false,
      pendingRecord: null,
      batchStatus: "idle",
      batchProgress: null,
      batchSummary: null,

      activePreset: () => {
        const { presets, activePresetId } = get();
        return presets.find((p) => p.id === activePresetId) ?? presets[0]!;
      },

      activeValues: () => get().activePreset().values,

      setAspect: (aspect) => set({ aspect }),
      setPanelCollapsed: (panelCollapsed) => set({ panelCollapsed }),
      setQueueCollapsed: (queueCollapsed) => set({ queueCollapsed }),
      setActivePresetId: (activePresetId) => set({ activePresetId }),

      updateActiveValues: (patch) => {
        const { presets, activePresetId } = get();
        set({
          presets: presets.map((p) => {
            if (p.id !== activePresetId) return p;
            const values = ShotPresetValuesSchema.parse({ ...p.values, ...patch });
            return { ...p, values };
          }),
        });
      },

      setEndAltitudeForAspect: (aspect, value) => {
        const { presets, activePresetId } = get();
        set({
          presets: presets.map((p) => {
            if (p.id !== activePresetId) return p;
            return {
              ...p,
              values: {
                ...p.values,
                endAltitudeByAspect: {
                  ...p.values.endAltitudeByAspect,
                  [aspect]: value,
                },
              },
            };
          }),
        });
      },

      savePresetAs: (name) => {
        const values = get().activeValues();
        const preset: NamedShotPreset = {
          id: newPresetId(),
          name: name.trim() || "Custom",
          values: structuredClone(values),
        };
        set({
          presets: [...get().presets, preset],
          activePresetId: preset.id,
        });
      },

      renameActivePreset: (name) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        set({
          presets: get().presets.map((p) =>
            p.id === get().activePresetId ? { ...p, name: trimmed } : p,
          ),
        });
      },

      setDedupeByCountry: (dedupeByCountry) => set({ dedupeByCountry }),
      setOutputFolderName: (outputFolderName) => set({ outputFolderName }),
      dismissCodecNotice: () => set({ codecNoticeDismissed: true }),
      setRecording: (recording) => set({ recording }),

      requestRecord: (pendingRecord) => set({ pendingRecord }),

      consumePendingRecord: () => {
        const t = get().pendingRecord;
        if (t) set({ pendingRecord: null });
        return t;
      },

      setQueue: (queue) => set({ queue }),

      addTargets: (targets, skipped) => {
        const existing = new Set(get().queue.map((t) => t.artistId));
        const next = [...get().queue];
        for (const t of targets) {
          if (existing.has(t.artistId)) continue;
          existing.add(t.artistId);
          next.push(t);
        }
        set({
          queue: next,
          skipped: [...get().skipped, ...skipped],
          queueCollapsed: false,
        });
      },

      removeFromQueue: (id) =>
        set({ queue: get().queue.filter((t) => t.id !== id) }),

      reorderQueue: (from, to) => {
        const queue = [...get().queue];
        if (from < 0 || from >= queue.length || to < 0 || to >= queue.length) return;
        const [item] = queue.splice(from, 1);
        if (!item) return;
        queue.splice(to, 0, item);
        set({ queue });
      },

      clearQueue: () => set({ queue: [], skipped: [] }),
      clearSkipped: () => set({ skipped: [] }),

      setBatchStatus: (batchStatus) => set({ batchStatus }),
      setBatchProgress: (batchProgress) => set({ batchProgress }),
      setBatchSummary: (batchSummary) => set({ batchSummary }),
    }),
    {
      name: "coykoi-globe-shot",
      partialize: (s) => ({
        presets: s.presets,
        activePresetId: s.activePresetId,
        aspect: s.aspect,
        panelCollapsed: s.panelCollapsed,
        queueCollapsed: s.queueCollapsed,
        dedupeByCountry: s.dedupeByCountry,
        codecNoticeDismissed: s.codecNoticeDismissed,
      }),
      merge: (persisted, current) => {
        const parsed = PersistedSchema.safeParse(persisted);
        if (!parsed.success) return current;
        const p = parsed.data;
        const presets = p.presets.length > 0 ? p.presets : SEED_SHOT_PRESETS;
        const activePresetId = presets.some((x) => x.id === p.activePresetId)
          ? p.activePresetId
          : presets[0]!.id;
        return {
          ...current,
          ...p,
          presets,
          activePresetId,
        };
      },
    },
  ),
);
